import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getGenericWhatsAppLink } from '../../utils/whatsapp';
import LanguageSwitcher from '../common/LanguageSwitcher';

// Official WhatsApp SVG Icon (chat bubble with phone)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, isEnglish } = useLanguage();
  const { t } = useTranslation();
  
  const isHomePage = location.pathname === '/' || location.pathname === '/en' || location.pathname === '/en/';
  const langPrefix = isEnglish ? '/en' : '';

  // Logo URL
  const logoUrl = 'https://res.cloudinary.com/dzx5jx8fr/image/upload/logo.png_fd1xab.jpg';

  // WhatsApp link with language-appropriate message
  const whatsappLink = getGenericWhatsAppLink(language);

  const navLinks = [
    { name: t('header.excursions'), href: isHomePage ? '#excursions' : `${langPrefix}/#excursions`, isAnchor: true },
    { name: t('header.packages'), href: `${langPrefix}/packs`, isAnchor: false },
    { name: t('header.howItWorks'), href: isHomePage ? '#how-it-works' : `${langPrefix}/#how-it-works`, isAnchor: true },
    { name: t('header.reviews'), href: isHomePage ? '#testimonials' : `${langPrefix}/#testimonials`, isAnchor: true },
    { name: t('header.faq'), href: isHomePage ? '#faq' : `${langPrefix}/#faq`, isAnchor: true },
    { name: isEnglish ? 'Who am I' : 'Qui suis-je', href: isEnglish ? '/en/who-am-i' : '/qui-suis-je', isAnchor: false },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isHomePage 
        ? 'bg-transparent' 
        : 'bg-white/95 backdrop-blur-sm border-b border-slate-100 shadow-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to={langPrefix || '/'} className="flex items-center gap-2">
            <img 
              src={logoUrl} 
              alt="Hurghada Excursions" 
              className="h-10 w-auto object-contain rounded-lg"
            />
            <span className={`font-semibold text-lg hidden sm:block ${isHomePage ? 'text-white drop-shadow-md' : 'text-slate-800'}`}>
              Hurghada Excursions
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isAnchor ? (
                <a
                  key={link.name}
                  href={link.href}
                  className={`transition-colors duration-200 text-sm font-medium ${
                    isHomePage 
                      ? 'text-white/90 hover:text-white drop-shadow' 
                      : 'text-slate-600 hover:text-teal-600'
                  }`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`transition-colors duration-200 text-sm font-medium ${
                    isHomePage 
                      ? 'text-white/90 hover:text-white drop-shadow' 
                      : 'text-slate-600 hover:text-teal-600'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTA + Language Switcher */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher className={isHomePage ? '[&_button]:text-white [&_button]:hover:bg-white/20 [&_span]:text-white/50' : ''} />
            
            {/* WhatsApp CTA */}
            <Button
              asChild
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 shadow-lg shadow-emerald-500/30 transition-all duration-300 hover:shadow-emerald-500/40 hover:scale-105"
            >
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon className="w-4 h-4 mr-2" />
                {t('header.whatsapp')}
              </a>
            </Button>
          </div>

          {/* Mobile: Language Switcher + Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            {/* Language Switcher - Always visible on mobile */}
            <LanguageSwitcher className={`${isHomePage ? '[&_button]:text-white [&_button]:hover:bg-white/20 [&_span]:text-white/50 [&_button.bg-teal-500]:bg-white/30 [&_button.bg-teal-500]:text-white' : ''}`} />
            
            {/* Menu Button - Higher z-index */}
            <button
              className={`p-2 transition-colors relative z-[60] ${
                isHomePage ? 'text-white hover:text-white/80' : 'text-slate-600 hover:text-teal-600'
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Outside container for full width */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg animate-in slide-in-from-top-2 duration-200 relative z-[55]">
          <nav className="flex flex-col gap-1 px-4 py-4 max-w-7xl mx-auto">
            {navLinks.map((link) => (
              link.isAnchor ? (
                <a
                  key={link.name}
                  href={link.href}
                  className="px-4 py-3 text-slate-600 hover:text-teal-600 hover:bg-slate-50 rounded-lg transition-all duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className="px-4 py-3 text-slate-600 hover:text-teal-600 hover:bg-slate-50 rounded-lg transition-all duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            ))}
            <div className="pt-3 px-4">
              <Button
                asChild
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg py-6"
              >
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <WhatsAppIcon className="w-4 h-4 mr-2" />
                  {t('header.chatOnWhatsapp')}
                </a>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
